import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Building2, MapPin, Star, Phone, Mail, Clock, Shield, ChevronLeft, Check, X, Stethoscope, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { getMe } from '../services/api';
import api from '../services/api';

export default function FacilityComparePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try { const r = await getMe(); setUser(r.data); } catch { setUser(null); }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const ids = searchParams.get('ids')?.split(',') || [];
    if (ids.length < 2) { navigate('/facilities'); return; }
    const load = async () => {
      try {
        const results = await Promise.all(ids.map(id => api.get(`/facilities/${id}`)));
        setFacilities(results.map(r => r.data));
      } catch { navigate('/facilities'); }
      finally { setLoading(false); }
    };
    load();
  }, [searchParams, navigate]);

  const getTypeColor = (type) => {
    const colors = {
      'Clinic': 'bg-blue-100 text-blue-700', 'Dental Clinic': 'bg-purple-100 text-purple-700',
      'Optical Clinic': 'bg-teal-100 text-teal-700', 'Hospital': 'bg-emerald-100 text-emerald-700',
      'Multi-specialty Hospital': 'bg-amber-100 text-amber-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  const allSpecialties = [...new Set(facilities.flatMap(f => Object.keys(f.specialties_available || {})))].sort();
  const allServices = [...new Set(facilities.flatMap(f => f.services_available || []))].sort();
  const allInsurances = [...new Set(facilities.flatMap(f => f.insurances_accepted || []))].sort();
  const allImaging = [...new Set(facilities.flatMap(f => f.imaging_modalities || []))].sort();

  const colWidth = facilities.length === 2 ? 'w-1/2' : 'w-1/3';

  const hourCategories = [
    { key: 'weekdays', label: 'Mon-Fri' }, { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }, { key: 'public_holidays', label: 'Holidays' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} />
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/facilities')} className="flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#0F2A44] mb-6 transition-colors" data-testid="back-to-facilities">
          <ChevronLeft className="h-4 w-4" /> Back to Facilities
        </button>

        <h1 className="font-manrope font-bold text-3xl text-[#0F2A44] mb-8" data-testid="compare-title">
          Compare Facilities
        </h1>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]" data-testid="compare-table">
            {/* Header Row - Facility Cards */}
            <thead>
              <tr>
                <th className="w-40 p-2 text-left align-top"></th>
                {facilities.map(f => (
                  <th key={f.facility_id} className={`${colWidth} p-2 align-top`}>
                    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                      {f.facility_images?.length > 0 ? (
                        <img src={f.facility_images[0]} alt={f.facility_name} className="w-full h-32 object-cover" />
                      ) : (
                        <div className="w-full h-32 bg-gradient-to-br from-[#0F2A44] to-[#1a3d5c] flex items-center justify-center">
                          <Building2 className="h-8 w-8 text-white/30" />
                        </div>
                      )}
                      <div className="p-4 text-center">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full mb-2 ${getTypeColor(f.facility_type)}`}>{f.facility_type}</span>
                        <h3 className="font-manrope font-bold text-[#0F2A44] text-base mb-1">
                          <Link to={`/facility/${f.facility_id}`} className="hover:text-[#2DD4BF]">{f.facility_name}</Link>
                        </h3>
                        <div className="flex items-center justify-center gap-1 text-sm">
                          <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" />
                          <span className="font-space font-bold">{(f.average_rating || 0).toFixed(1)}</span>
                          <span className="text-xs text-[#4B5563]">({f.total_reviews || 0})</span>
                        </div>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="text-sm">
              {/* Basic Info Section */}
              <SectionHeader title="Basic Info" />
              <CompareRow label="Location" facilities={facilities} render={f => (
                <div className="flex items-start gap-1"><MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-[#4A90E2]" /><span>{f.location}, {f.region}</span></div>
              )} />
              <CompareRow label="Hospital Level" facilities={facilities} render={f => f.hospital_level || '—'} />
              <CompareRow label="Phone" facilities={facilities} render={f => f.contact_phone || '—'} />
              <CompareRow label="KMPDC" facilities={facilities} render={f => (
                f.kmpdc_registration ? (
                  <span className={`px-2 py-0.5 text-xs rounded-full ${f.kmpdc_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {f.kmpdc_verified ? 'Verified' : 'Pending'}
                  </span>
                ) : '—'
              )} />

              {/* Hours Section */}
              <SectionHeader title="Working Hours" />
              <CompareRow label="24/7" facilities={facilities} render={f => (
                f.is_24_7 ? <span className="text-green-600 font-semibold">Yes</span> : <span className="text-slate-400">No</span>
              )} />
              {hourCategories.map(({ key, label }) => (
                <CompareRow key={key} label={label} facilities={facilities} render={f => {
                  if (f.is_24_7) return <span className="text-green-600">24/7</span>;
                  const val = f.opening_hours?.[key];
                  if (!val) return '—';
                  return <span className={val === 'Closed' ? 'text-red-400' : ''}>{val}</span>;
                }} />
              ))}

              {/* Ratings Section */}
              <SectionHeader title="Ratings" />
              <CompareRow label="Overall" facilities={facilities} render={f => (
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" />
                  <span className="font-space font-bold text-lg">{(f.average_rating || 0).toFixed(1)}</span>
                </div>
              )} />
              <CompareRow label="Reviews" facilities={facilities} render={f => `${f.total_reviews || 0} reviews`} />

              {/* Signature Services */}
              <SectionHeader title="Signature Services" />
              <tr>
                <td className="p-3 font-medium text-[#4B5563] align-top border-b border-slate-100"></td>
                {facilities.map(f => (
                  <td key={f.facility_id} className="p-3 align-top border-b border-slate-100">
                    <div className="flex flex-wrap gap-1">
                      {(f.signature_services || []).map((s, i) => (
                        <span key={i} className="px-2 py-0.5 bg-[#2DD4BF]/10 text-[#0F2A44] text-xs rounded-full font-medium">{s}</span>
                      ))}
                      {(!f.signature_services || f.signature_services.length === 0) && <span className="text-slate-400">—</span>}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Specialties Comparison */}
              {allSpecialties.length > 0 && (
                <>
                  <SectionHeader title="Specialties" />
                  {allSpecialties.map(spec => (
                    <CompareRow key={spec} label={spec} facilities={facilities} render={f => {
                      const has = f.specialties_available?.[spec];
                      return has ? <Check className="h-5 w-5 text-[#2DD4BF]" /> : <X className="h-5 w-5 text-slate-200" />;
                    }} />
                  ))}
                </>
              )}

              {/* Imaging Comparison */}
              {allImaging.length > 0 && (
                <>
                  <SectionHeader title="Imaging & Diagnostics" />
                  {allImaging.map(mod => (
                    <CompareRow key={mod} label={mod} facilities={facilities} render={f => {
                      const has = (f.imaging_modalities || []).includes(mod);
                      return has ? <Check className="h-5 w-5 text-[#2DD4BF]" /> : <X className="h-5 w-5 text-slate-200" />;
                    }} />
                  ))}
                </>
              )}

              {/* Services Comparison */}
              {allServices.length > 0 && (
                <>
                  <SectionHeader title="Services" />
                  {allServices.map(svc => (
                    <CompareRow key={svc} label={svc} facilities={facilities} render={f => {
                      const has = (f.services_available || []).includes(svc);
                      return has ? <Check className="h-5 w-5 text-[#2DD4BF]" /> : <X className="h-5 w-5 text-slate-200" />;
                    }} />
                  ))}
                </>
              )}

              {/* Insurances Comparison */}
              {allInsurances.length > 0 && (
                <>
                  <SectionHeader title="Insurances Accepted" />
                  {allInsurances.map(ins => (
                    <CompareRow key={ins} label={ins} facilities={facilities} render={f => {
                      const has = (f.insurances_accepted || []).includes(ins);
                      return has ? <Check className="h-5 w-5 text-[#2DD4BF]" /> : <X className="h-5 w-5 text-slate-200" />;
                    }} />
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <tr>
      <td colSpan={4} className="pt-6 pb-2 px-3">
        <h3 className="font-manrope font-bold text-[#0F2A44] text-base border-b-2 border-[#2DD4BF] pb-2">{title}</h3>
      </td>
    </tr>
  );
}

function CompareRow({ label, facilities, render }) {
  return (
    <tr className="hover:bg-slate-50/50">
      <td className="p-3 font-medium text-[#4B5563] text-xs align-middle border-b border-slate-100 whitespace-nowrap">{label}</td>
      {facilities.map(f => (
        <td key={f.facility_id} className="p-3 text-[#262626] align-middle border-b border-slate-100">{render(f)}</td>
      ))}
    </tr>
  );
}
